import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useOutletContext } from 'react-router';
import { api } from '@/api/client';
import { logoutCurrentDevice } from '@/shell/logout';
import { useSession } from '@/stores/session';
import { loadFavorites, localFavorites, saveFavorites } from '@/stores/favorites';
import { APPEARANCE_PRESETS, useAppearance } from '@/stores/appearance';
import { Icon } from '@/components/Icon';
import { Empty } from '@/components/States';
import { NAV, canSeeNavItem, legacyHref, type NavItem } from '@/shell/nav';
import s from './Pages.module.css';

const APP_ALIASES: Record<string, string> = {
  shipments: '发货 出库 批次 物流', exceptions: '异常 售后', purchase: '采购 入库',
  inventory: '库存 仓库', settlements: '结算 对账 对账单 应收 应付',
  reports: '报表 统计 账龄', taobao: '淘宝 店铺', products: '产品 画册',
  customers: '客户 供应商', settings: '设置 管理', orders: '订单 销售',
};

function defaultFavorites(role?: string) {
  return role === 'WAREHOUSE' ? ['shipments', 'exceptions', 'purchase', 'orders'] : ['shipments', 'exceptions', 'purchase', 'settlements'];
}

function moveFavorite(keys: string[], from: string, to: string) {
  if (from === to || !keys.includes(from) || !keys.includes(to)) return keys;
  const next = keys.filter((key) => key !== from);
  next.splice(next.indexOf(to), 0, from);
  return next;
}

/** 手机「应用」页：全部模块、搜索与常用编辑；只列有权限的模块。 */
export function AppsPage() {
  const can = useSession((st) => st.can);
  const user = useSession((st) => st.user);
  const [query, setQuery] = useState('');
  const [editing, setEditing] = useState(false);
  const [saved, setSaved] = useState<string[] | null>(null);
  const [draft, setDraft] = useState<string[]>([]);
  const [editError, setEditError] = useState('');
  const dragFrom = useRef<string | null>(null);
  const touchAt = useRef(0);
  const suppressClick = useRef(false);
  const saveRevision = useRef(0);
  const shell = useOutletContext<{
    setPhoneTabBarHidden?: (hidden: boolean) => void;
    navBadges?: Record<string, number | undefined>;
  } | undefined>();
  const role = user?.role;
  const items = NAV.filter((i) => i.key !== 'home' && canSeeNavItem(i, can, role));
  const allowed = new Set(items.map((item) => item.key));
  const storageKey = user ? `ims.v2.favorites.${user.id}` : '';
  useEffect(() => {
    if (!storageKey) return;
    const userId = user!.id;
    setSaved(localFavorites(userId));
    const revision = saveRevision.current;
    let active = true;
    void loadFavorites(userId).then((keys) => {
      if (active && revision === saveRevision.current) setSaved(keys);
    });
    return () => { active = false; };
  }, [storageKey]);
  useEffect(() => {
    shell?.setPhoneTabBarHidden?.(editing);
    return () => shell?.setPhoneTabBarHidden?.(false);
  }, [editing, shell?.setPhoneTabBarHidden]);

  const defaults = defaultFavorites(role).filter((key) => allowed.has(key));
  const favorites = (saved || defaults).filter((key) => allowed.has(key));
  const shown = editing ? draft : favorites;
  const searched = items.filter((item) => `${item.label} ${item.short} ${APP_ALIASES[item.key] || ''}`.toLowerCase().includes(query.trim().toLowerCase()));

  const tile = (item: NavItem, editable = false, favorite = false) => {
    const badge = !editable && item.key === 'exceptions' ? shell?.navBadges?.exceptions : undefined;
    const inner = <>
      {editable && (favorite || !draft.includes(item.key)) && <span className={`${s.pin} ${favorite ? s.remove : s.add}`}><Icon name={favorite ? 'minus' : 'plus'} size={14} /></span>}
      <span className={`${s.tile} ${item.tone ? s[item.tone] : ''}`}><Icon name={item.icon} size={24} /></span>
      {badge !== undefined && badge > 0 && <i aria-label={`${badge} 个待处理异常`}>{badge}</i>}
      <b>{item.short}</b>
    </>;
    const className = `${s.app} ${editable && !favorite && draft.includes(item.key) ? s.dim : ''}`;
    if (editable) return <button key={item.key} type="button" className={className} disabled={!favorite && draft.includes(item.key)} onClick={() => {
      if (suppressClick.current) return;
      setEditError('');
      if (favorite) setDraft((current) => current.filter((key) => key !== item.key));
      else if (draft.length >= 8) setEditError('常用最多添加 8 个模块。');
      else setDraft((current) => [...current, item.key]);
    }}>{inner}</button>;
    const ariaLabel = badge !== undefined && badge > 0 ? `${item.short}，${badge} 个待处理异常` : undefined;
    return item.to ? <Link key={item.key} to={item.to} className={className} aria-label={ariaLabel}>{inner}</Link> : <a key={item.key} href={legacyHref(item.legacy)} className={className} aria-label={ariaLabel}>{inner}</a>;
  };
  const group = (g: 'biz' | 'data', title: string) => {
    const rows = (editing ? items : searched).filter((item) => item.group === g);
    if (!rows.length) return null;
    return <section className={s.sec} key={g}>
      <h2>{title}{editing && g === 'biz' && <span>点 + 加入常用</span>}</h2>
      <div className={s.grid}>{rows.map((item) => tile(item, editing))}</div>
    </section>;
  };

  const startEdit = () => { setQuery(''); setDraft(favorites); setEditError(''); setEditing(true); };
  const finishEdit = () => {
    if (!user) return;
    saveRevision.current += 1;
    setSaved(draft);
    setEditing(false);
    void saveFavorites(user.id, draft).then((result) => {
      if (result === 'local' && import.meta.env.VITE_READONLY === '0') setEditError('已保存在本机，账号同步失败；稍后再试。');
    }).catch((error) => setEditError(error instanceof Error ? error.message : '常用设置保存失败'));
  };

  return (
    <div className={s.page}>
      <header className={`${s.head} ${s.appHead}`}>
        {editing ? <><button type="button" onClick={() => { setEditError(''); setEditing(false); }}>取消</button><h1>编辑常用</h1><button type="button" className={s.blueText} onClick={finishEdit}>完成</button></>
          : <><h1>应用</h1><button type="button" className={s.blueText} onClick={startEdit}>编辑</button></>}
      </header>
      <div className={s.scroll}>
        {!editing && <label className={s.appSearch}><Icon name="srch" size={20} /><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="搜索模块，如 发货、对账" aria-label="搜索模块" /></label>}
        {editError && <p className={s.editError} role="status">{editError}</p>}
        {!query && <section className={s.sec}>
          <h2>常用{editing ? ` · ${draft.length} / 8` : ''}<span>{editing ? '按住拖动排序' : '点「编辑」调整'}</span></h2>
          {shown.length ? <div className={s.grid}>{shown.map((key) => {
            const item = items.find((row) => row.key === key)!;
            return editing ? <div key={key} className={s.draggable} data-fav={key} draggable
              onDragStart={() => { dragFrom.current = key; }}
              onDragOver={(event) => event.preventDefault()}
              onDrop={() => { setDraft((current) => moveFavorite(current, dragFrom.current || '', key)); dragFrom.current = null; }}
              onTouchStart={() => { dragFrom.current = key; touchAt.current = Date.now(); }}
              onTouchEnd={(event) => {
                const touch = event.changedTouches[0];
                const target = document.elementFromPoint(touch.clientX, touch.clientY)?.closest('[data-fav]');
                const to = target?.getAttribute('data-fav') || '';
                if (Date.now() - touchAt.current >= 300 && to && to !== key) {
                  suppressClick.current = true;
                  setDraft((current) => moveFavorite(current, dragFrom.current || '', to));
                  window.setTimeout(() => { suppressClick.current = false; }, 350);
                }
                dragFrom.current = null;
              }}>{tile(item, true, true)}</div> : tile(item);
          })}</div> : <p className={s.noFavorites}>还没有常用模块，点「编辑」添加。</p>}
        </section>}
        {group('biz', '业务')}
        {group('data', '档案')}
        {!editing && query && !searched.length && <p className={s.noFavorites}>没有匹配的模块，试试别的名称。</p>}
        <p className={s.note}>{editing ? `常用只改变这一页的顺序，不改底部 Tab；${import.meta.env.VITE_READONLY === '0' ? '完成后按账号同步。' : '只读期保存在本机。'}` : `只列你有权限的模块 · ${user?.name || ''} · ${user?.roleName || user?.role || ''} · ${items.length} 个`}</p>
      </div>
    </div>
  );
}

export function MePage() {
  const user = useSession((st) => st.user);
  const appearance = useAppearance((st) => st.value);
  const appearanceName = APPEARANCE_PRESETS.find((preset) => preset.value.fontSize === appearance.fontSize && preset.value.lineHeight === appearance.lineHeight && preset.value.gap === appearance.gap)?.label || '自定义';
  const nav = useNavigate();
  const [push, setPush] = useState('查询中…');
  const [sessionStarted, setSessionStarted] = useState('');
  const [confirmLogout, setConfirmLogout] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);
  const [logoutError, setLogoutError] = useState('');
  const standalone = window.matchMedia('(display-mode: standalone)').matches || (navigator as Navigator & { standalone?: boolean }).standalone === true;
  const scope = user?.permissions.includes('orders.read.all') ? '全部数据'
    : user?.permissions.includes('orders.read.own') ? '本人创建'
      : user?.permissions.includes('orders.read.assigned') ? '分配给我' : '按角色授权';
  const device = /iPad/i.test(navigator.userAgent) ? '这台 iPad' : /iPhone/i.test(navigator.userAgent) ? '这台 iPhone'
    : /Android/i.test(navigator.userAgent) ? '这台安卓设备' : '这台电脑';
  useEffect(() => {
    let active = true;
    void api.get<{ createdAt: string | null }>('/api/v2/me/session-info')
      .then(({ createdAt }) => {
        if (!active || !createdAt) return;
        const date = new Date(createdAt);
        setSessionStarted(new Intl.DateTimeFormat('zh-CN', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false }).format(date));
      }).catch(() => undefined);
    return () => { active = false; };
  }, []);
  useEffect(() => {
    let active = true;
    const inspect = async () => {
      if (!('Notification' in window) || !('serviceWorker' in navigator)) { if (active) setPush('此浏览器不支持'); return; }
      if (Notification.permission !== 'granted') { if (active) setPush('推送未开启'); return; }
      try {
        const registration = await navigator.serviceWorker.getRegistration('/');
        const script = registration?.active?.scriptURL || registration?.waiting?.scriptURL || registration?.installing?.scriptURL;
        if (!registration || !script || new URL(script).pathname !== '/sw.js' || new URL(registration.scope).pathname !== '/') { if (active) setPush('推送未开启'); return; }
        const subscription = await registration.pushManager?.getSubscription();
        if (active) setPush(subscription ? '推送已开启' : '推送未开启');
      } catch { if (active) setPush('状态不可用'); }
    };
    void inspect();
    return () => { active = false; };
  }, []);
  const logout = async () => {
    setLoggingOut(true);
    setLogoutError('');
    try {
      await logoutCurrentDevice();
      nav('/', { replace: true });
    } catch (error) {
      setLogoutError(error instanceof Error ? error.message : '退出失败，请重试。');
    } finally { setLoggingOut(false); }
  };
  return (
    <div className={s.page}>
      <header className={s.head}><h1>我的</h1></header>
      <div className={s.scroll}>
        <div className={s.card}>
          <span className={s.ava}>{user?.name.slice(0, 1)}</span>
          <div><b>{user?.name}<span className={s.roleTag}>{user?.roleName || user?.role}</span></b><small>{user?.username} · 数据范围：{scope}</small><small>本次登录 {sessionStarted || '当前会话'} · {device}</small></div>
        </div>
        <section className={s.meSection}>
          <h2>个人设置</h2>
          <div className={s.meList}>
            <Link className={s.meRow} to="/me/appearance"><span className={s.meIcon}><Icon name="type" /></span><b>外观与密度</b><span className={s.meValue}>{appearanceName} {appearance.fontSize} / {appearance.lineHeight} / {appearance.gap}<Icon name="chev" size={14} /></span></Link>
            <Link className={s.meRow} to="/me/notifications"><span className={s.meIcon}><Icon name="bell" /></span><b>通知与声音</b><span className={`${s.meValue} ${push === '推送未开启' ? s.meAttention : ''}`}>{push}<Icon name="chev" size={14} /></span></Link>
          </div>
        </section>
        <section className={s.meSection}>
          <h2>账号与设备</h2>
          <div className={s.meList}>
            <Link className={s.meRow} to="/me/password"><span className={s.meIcon}><Icon name="lock" /></span><b>修改密码</b><span className={s.meValue}><Icon name="chev" size={14} /></span></Link>
            <Link className={s.meRow} to="/me/install"><span className={s.meIcon}><Icon name="home" /></span><b>添加到主屏幕</b><span className={s.meValue}>{standalone ? '已添加' : '未添加'}<Icon name="chev" size={14} /></span></Link>
            <Link className={s.meRow} to="/me/about"><span className={s.meIcon}><Icon name="info" /></span><b>关于</b><span className={s.meValue}>v2.0.0<Icon name="chev" size={14} /></span></Link>
          </div>
        </section>
        <button type="button" className={s.exit} onClick={() => setConfirmLogout(true)}><Icon name="logout" />退出登录</button>
        <p className={s.note}>新版只读体验期 · 权限 {user?.permissions.length} 项 · 与老版共用登录 · <a href={legacyHref('/dashboard')}>回到老版</a></p>
      </div>
      {confirmLogout && <div className={s.logoutScrim} onClick={() => !loggingOut && setConfirmLogout(false)}>
        <section className={s.logoutSheet} role="dialog" aria-modal="true" aria-label="退出登录确认" onClick={(event) => event.stopPropagation()}>
          <h2>退出登录？</h2>
          <p>{device}将不再收到{user?.name || '当前账号'}的推送；本机外观偏好会保留，下次登录照旧。</p>
          {logoutError && <p className={s.logoutError} role="alert">{logoutError}</p>}
          <button type="button" className={s.logoutDanger} disabled={loggingOut} onClick={() => void logout()}>{loggingOut ? '正在退出…' : '退出登录'}</button>
          <button type="button" className={s.logoutCancel} disabled={loggingOut} onClick={() => setConfirmLogout(false)}>取消</button>
        </section>
      </div>}
    </div>
  );
}

export function NotFoundPage() {
  return (
    <div className={s.page}>
      <Empty icon="info" title="这个页面新版还没有" action={<Link to="/" className={s.btn}>回主屏</Link>}>
        新版正在逐个模块补齐，其余功能先在老版使用。
      </Empty>
    </div>
  );
}
